export type LxAttributionContext = Record<string, string>;

// 同一个浏览器标签页内的所有 BrowserRouter path 共用一份归因上下文，不按 path 分桶。
const STORAGE_KEY = 'sailor_c_lx_attribution';

// SPA Runtime 内优先从内存读取，页面刷新后再由 sessionStorage 恢复。
let attributionContext: LxAttributionContext = {};

const isAttributionKey = (key: string): boolean => {
  const normalizedKey = key.toLowerCase();
  return normalizedKey.startsWith('utm_') || normalizedKey === 'lch' || normalizedKey === 'channel';
};

export const parseLxAttributionParams = (search: string): LxAttributionContext => {
  const params: LxAttributionContext = {};

  // BrowserRouter 的入口参数位于 location.search，不解析 hash。
  new URLSearchParams(search).forEach((value, key) => {
    const normalizedValue = value.trim();

    if (isAttributionKey(key) && normalizedValue) {
      // 判断时忽略大小写，上报时保留入口 URL 中的原始字段名。
      params[key] = normalizedValue;
    }
  });

  return params;
};

const readStoredContext = (): LxAttributionContext => {
  try {
    const storedValue = window.sessionStorage.getItem(STORAGE_KEY);
    if (!storedValue) {
      return {};
    }

    const storedParams = JSON.parse(storedValue);
    return storedParams && typeof storedParams === 'object' && !Array.isArray(storedParams) ? storedParams : {};
  } catch (error) {
    return {};
  }
};

const storeContext = (params: LxAttributionContext): void => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(params));
  } catch (error) {
    // sessionStorage 不可用时，当前 SPA Runtime 内仍使用内存上下文。
    console.error(error);
  }
};

export const getLxAttributionContext = (): LxAttributionContext => ({
  ...attributionContext,
});

export const initLxAttributionContext = (tracker?: Function | null): LxAttributionContext => {
  const urlParams = parseLxAttributionParams(window.location.search);

  if (Object.keys(urlParams).length) {
    // 当前入口显式携带归因参数时整体覆盖旧快照，避免新旧渠道字段混合。
    attributionContext = urlParams;
    storeContext(attributionContext);
  } else {
    // 页面刷新或同源整页打开且 URL 未携带归因参数时，继续沿用当前会话快照。
    attributionContext = readStoredContext();
  }

  if (tracker && Object.keys(attributionContext).length) {
    // 写入 Tracker 环境公共字段；val_lab.custom 在公共灵犀封装中读取同一快照。
    tracker('set', getLxAttributionContext());
  }

  return getLxAttributionContext();
};

export const clearLxAttributionContext = (): void => {
  attributionContext = {};
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    // 忽略存储清理失败。
    console.error(error);
  }
};
